import { useCallback, useEffect, useState } from "react";
import {
    Button,
    DatePicker,
    Empty,
    Input,
    Modal,
    Pagination,
    Popconfirm,
    Spin,
    Typography,
    message,
} from "antd";
import {
    PlusOutlined,
    CopyOutlined,
    DeleteOutlined,
    EyeOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import PageTitle from "../../components/title/pageTitle";
import Navigation from "../../components/navigation/navigation";
import Footer from "../../components/footer/footer";
import BackHomeIcon, { PANEL_PATH } from "../../components/icons/BackHome/backhome";
import BookingForm from "../../components/booking/BookingForm";
import BookingPosterPanel from "../../components/booking/BookingPosterPanel";
import {
    BOOKING_DEFAULT_PAGE_SIZE,
    BOOKING_MAX_PAGE_SIZE,
    createBooking,
    deleteBooking,
    getBookingPublicUrl,
    listBookings,
} from "../../utils/bookingApi";
import { serializeShootAt, formatShootAtDisplay, formatCreatedAt } from "../../utils/bookingFormat";
import { useTranslation } from "../../i18n";
import "../selection/selection.css";
import "./booking.css";

const { Title, Paragraph, Text } = Typography;

function Bookings() {
    const { t } = useTranslation();
    const [bookings, setBookings] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [formOpen, setFormOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [createdBooking, setCreatedBooking] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(BOOKING_DEFAULT_PAGE_SIZE);
    const [hasMore, setHasMore] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState(null);
    const [appliedQuery, setAppliedQuery] = useState({ q: "", shootFrom: null, shootTo: null });

    // 404 视为「确实不存在」，不追加网络排查提示；其余按网络问题处理。
    const buildErrorMessage = useCallback(
        (baseKey, error) => {
            const base = t(baseKey);
            if (error?.status === 404) {
                return base;
            }
            return `${base} ${t("common.networkTroubleshootHint")}`;
        },
        [t]
    );

    const loadBookings = useCallback(async () => {
        setLoadingList(true);
        try {
            const result = await listBookings({
                q: appliedQuery.q || undefined,
                shootFrom: appliedQuery.shootFrom ?? undefined,
                shootTo: appliedQuery.shootTo ?? undefined,
                page,
                pageSize,
            });
            setBookings(result.bookings);
            setHasMore(result.hasMore);
        } catch (error) {
            console.error("Load bookings failed:", error);
            message.error(buildErrorMessage("booking.admin.loadFailed", error));
        } finally {
            setLoadingList(false);
        }
    }, [appliedQuery, page, pageSize, buildErrorMessage]);

    useEffect(() => {
        loadBookings();
    }, [loadBookings]);

    const handleSearch = useCallback(() => {
        const shootFrom = dateRange?.[0] ? dateRange[0].format("YYYY-MM-DD") : null;
        const shootTo = dateRange?.[1] ? dateRange[1].format("YYYY-MM-DD") : null;
        setPage(1);
        setAppliedQuery({
            q: searchText.trim(),
            shootFrom,
            shootTo,
        });
    }, [dateRange, searchText]);

    const handleResetFilters = useCallback(() => {
        setSearchText("");
        setDateRange(null);
        setPage(1);
        setAppliedQuery({ q: "", shootFrom: null, shootTo: null });
    }, []);

    const handleCreate = useCallback(
        async (values) => {
            const shootAt = serializeShootAt(values.shootAt);
            if (!shootAt) {
                message.error(t("booking.form.shootAtRequired"));
                return;
            }
            setSubmitting(true);
            try {
                const booking = await createBooking({
                    title: values.title,
                    location: values.location,
                    shootAt,
                    content: values.content,
                    duration: values.duration || "",
                    notes: values.notes || "",
                });
                setFormOpen(false);
                setCreatedBooking(booking);
                message.success(t("booking.admin.createDone"));
                loadBookings();
            } catch (error) {
                console.error("Create booking failed:", error);
                message.error(buildErrorMessage("booking.admin.createFailed", error));
            } finally {
                setSubmitting(false);
            }
        },
        [t, loadBookings, buildErrorMessage]
    );

    const handleCopyLink = useCallback(
        async (id) => {
            const url = getBookingPublicUrl(id);
            try {
                await navigator.clipboard.writeText(url);
                message.success(t("booking.admin.linkCopied"));
            } catch {
                message.warning(url);
            }
        },
        [t]
    );

    const handleDelete = useCallback(
        async (id) => {
            try {
                await deleteBooking(id);
                message.success(t("booking.admin.deleteDone"));
                if (createdBooking?.id === id) {
                    setCreatedBooking(null);
                }
                loadBookings();
            } catch (error) {
                console.error("Delete booking failed:", error);
                message.error(buildErrorMessage("booking.admin.deleteFailed", error));
            }
        },
        [t, loadBookings, createdBooking, buildErrorMessage]
    );

    const paginationTotal = hasMore ? page * pageSize + 1 : (page - 1) * pageSize + bookings.length;

    const hasActiveFilters = Boolean(appliedQuery.q || appliedQuery.shootFrom || appliedQuery.shootTo);

    return (
        <>
            <PageTitle>
                <Navigation />
            </PageTitle>
            <BackHomeIcon to={PANEL_PATH} />
            <div className="booking-admin">
                <div className="booking-admin__header">
                    <Title level={2} style={{ margin: 0 }}>
                        {t("booking.admin.title")}
                    </Title>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setFormOpen(true)}>
                        {t("booking.admin.new")}
                    </Button>
                </div>
                <Paragraph type="secondary">{t("booking.admin.subtitle")}</Paragraph>

                <div className="selection-results__toolbar">
                    <div className="selection-results__filters">
                        <Input
                            allowClear
                            value={searchText}
                            onChange={(event) => setSearchText(event.target.value)}
                            onPressEnter={handleSearch}
                            placeholder={t("booking.admin.searchPlaceholder")}
                            className="selection-results__search"
                        />
                        <DatePicker.RangePicker
                            value={dateRange}
                            onChange={setDateRange}
                            allowEmpty={[true, true]}
                            className="selection-results__date-range"
                            placeholder={[t("booking.admin.dateFrom"), t("booking.admin.dateTo")]}
                        />
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                            {t("booking.admin.search")}
                        </Button>
                        {hasActiveFilters ? (
                            <Button onClick={handleResetFilters}>{t("booking.admin.resetFilters")}</Button>
                        ) : null}
                    </div>
                    <Button icon={<ReloadOutlined />} onClick={loadBookings}>
                        {t("booking.admin.refresh")}
                    </Button>
                </div>

                {loadingList ? (
                    <div className="booking-admin__center">
                        <Spin />
                    </div>
                ) : bookings.length === 0 ? (
                    <Empty
                        description={
                            hasActiveFilters ? t("booking.admin.emptyFiltered") : t("booking.admin.empty")
                        }
                    />
                ) : (
                    <>
                        <div className="booking-admin__list">
                            {bookings.map((item) => (
                                <div key={item.id} className="booking-admin__item">
                                    <div className="booking-admin__item-main">
                                        <Text strong className="booking-admin__item-title">
                                            {item.title}
                                        </Text>
                                        <Text type="secondary" className="booking-admin__item-meta">
                                            {formatShootAtDisplay(item.shootAt)} · {item.location}
                                        </Text>
                                        <Text type="secondary" className="booking-admin__item-created">
                                            {t("booking.admin.createdAt")} {formatCreatedAt(item.createdAt)}
                                        </Text>
                                    </div>
                                    <div className="booking-admin__item-actions">
                                        <Button
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => setCreatedBooking(item)}
                                        >
                                            {t("booking.admin.poster")}
                                        </Button>
                                        <Button
                                            size="small"
                                            icon={<CopyOutlined />}
                                            onClick={() => handleCopyLink(item.id)}
                                        >
                                            {t("booking.admin.copyLink")}
                                        </Button>
                                        <Popconfirm
                                            title={t("booking.admin.deleteConfirm")}
                                            onConfirm={() => handleDelete(item.id)}
                                            okText={t("booking.admin.deleteOk")}
                                            cancelText={t("booking.admin.deleteCancel")}
                                        >
                                            <Button size="small" danger icon={<DeleteOutlined />} />
                                        </Popconfirm>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="selection-results__pagination">
                            <Pagination
                                current={page}
                                pageSize={pageSize}
                                total={paginationTotal}
                                showSizeChanger
                                pageSizeOptions={[10, 20, BOOKING_MAX_PAGE_SIZE]}
                                onChange={(nextPage, nextPageSize) => {
                                    setPage(nextPage);
                                    setPageSize(nextPageSize);
                                }}
                                showTotal={(total, range) => {
                                    const base = t("booking.admin.pageRange", {
                                        from: range[0],
                                        to: range[1],
                                    });
                                    return hasMore ? `${base}${t("booking.admin.hasMoreSuffix")}` : base;
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            <Modal
                open={formOpen}
                title={t("booking.admin.new")}
                onCancel={() => setFormOpen(false)}
                footer={null}
                destroyOnClose
                width={520}
                styles={{ body: { maxHeight: "min(78vh, 720px)", overflowY: "auto" } }}
            >
                <BookingForm onSubmit={handleCreate} submitting={submitting} />
            </Modal>

            <Modal
                open={Boolean(createdBooking)}
                title={t("booking.admin.posterTitle")}
                onCancel={() => setCreatedBooking(null)}
                footer={null}
                width={480}
                destroyOnClose
            >
                {createdBooking && (
                    <>
                        <Paragraph type="secondary" className="booking-admin__link">
                            <Text copyable={{ text: getBookingPublicUrl(createdBooking.id) }}>
                                {getBookingPublicUrl(createdBooking.id)}
                            </Text>
                        </Paragraph>
                        <BookingPosterPanel
                            booking={createdBooking}
                            publicUrl={getBookingPublicUrl(createdBooking.id)}
                        />
                    </>
                )}
            </Modal>
            <Footer />
        </>
    );
}

export default Bookings;
